import { useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { getSubscriptionTitle } from "../../../helpers/getSubscriptionTitle";
import Text from "../../atoms/Text/Text";
import Title from "../../atoms/Title/Title";
import { RootState } from "../../../types/rtk/api-config/types";
import { useAppSelector, useAppDispatch } from "../../../rtk/_config/hooks";
import { formatPrice } from "../../../helpers/currencyHelper";
import ModalPromotionalCode from "../ModalSubscriptionGiftCard/ModalSubscriptionGiftCard";
import { setBasket } from "../../../rtk/basket/basket";

interface BasketProps {
  onValidatePromoCode: (code: string | null) => Promise<boolean>;
}

const Basket = ({ onValidatePromoCode }: BasketProps) => {
  const { t } = useTranslation();
  const basket = useAppSelector((state: RootState) => state.basket);
  const subscription_offers = useAppSelector((state: RootState) => state.offers.subscriptionsData);
  const options_offers = useAppSelector((state: RootState) => state.offers.optionsData);
  const [isOpen, setIsOpen] = useState(true);
  const [isModalOpenPromoCode, setIsModalOpenPromoCode] = useState(false);
  const dispatch = useAppDispatch();

  const handleOpenPromoCode = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent accordion toggle click (that would close the basket)
    setIsModalOpenPromoCode(true);
  };
  const handleClosePromoCode = () => {
    setIsModalOpenPromoCode(false);
  };

  // Validate promo code and close modal if successful
  const handleValidatePromoCode = async (code: string | null) => {
    const success = await onValidatePromoCode(code);
    if (success) {
      setIsModalOpenPromoCode(false);
      setIsOpen(true); // Reopen the basket after validating the promo code
    }
    return success;
  };

  // Remove promo code from basket
  const handleRemovePromoCode = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent accordion toggle click (that would close the basket)
    dispatch(setBasket({
      ...basket,
      promo: null,
      promocode_ratio: null
    }));
  };

  // Calculate prices and reductions
  const selectedOffer = subscription_offers?.find(offer => offer.id === basket.offer_id);
  const selectedOptionsOffers = options_offers?.filter(opt => basket.options?.includes(opt.id)) || [];
  const sub_name = getSubscriptionTitle(selectedOffer?.period ?? 0);
  const sub_currency = selectedOffer?.price.currency || "EUR";
  const sub_price = selectedOffer?.price.amount || 0;
  const sub_price_giftcard_applied = sub_price * (1 - (basket.giftcard_ratio || 0));

  const giftcard_reduction = 100 * (basket.giftcard_ratio || 0);
  const promocode_amount = sub_price * (basket.promocode_ratio || 0);

  const totalPrice = sub_price_giftcard_applied
    - promocode_amount
    + selectedOptionsOffers.reduce((acc, opt) => acc + (opt.code === 'premium_pack' ? 0 : opt.price.amount), 0);


  return (
    <Box className="Basket">
      <Box className="offerChoosen">
        <Accordion
          className="Accordion"
          expanded={isOpen}
          onClick={() => setIsOpen(!isOpen)}
        >
          <AccordionSummary
            id="panel-header"
            aria-controls="panel-content"
            expandIcon={<ExpandMoreIcon />}
          />
          <AccordionDetails>
            <Box className="subopt">
              <Box className="text">
                {basket.giftcard ? (
                  <>
                    <Text>{t("subscription:FIRST_PAGE.MODAL_GIFT_CARD.INPUT_LABEL")} : {basket.giftcard}</Text>
                    <Box className="subTitleBox">
                      <Text className="subTitle">{sub_name} {
                        (giftcard_reduction < 100)
                          ? t("subscription:FOR_MINUS_X_PERCENT", { percent: giftcard_reduction })
                          : t("subscription:FREE")}</Text>
                    </Box>
                  </>
                ) : <Text children={sub_name} />
                }
              </Box>
              <Box className="price">
                <Text children={(sub_price_giftcard_applied > 0.0) ? formatPrice(sub_price_giftcard_applied, sub_currency) : t("subscription:FREE")} />
                {basket.giftcard &&
                  <Text className="old-price" children={formatPrice(sub_price || 0, sub_currency)} />
                }
              </Box>
              </Box>

              {selectedOptionsOffers.map((opt, i) => (
                <Box key={i} className="subopt">
                    <Box className="text">
                      <Text
                        className="title"
                        children={t(
                          `subscription:SECOND_PAGE.OPTION_CHOICE.TITLE.${opt.code}`
                        )}
                      />
                    </Box>
                    <Box className="price">
                      <Text
                        children={
                          opt.code === "premium_pack"
                            ? t("subscription:FREE")
                            : formatPrice(opt.price.amount, opt.price.currency)
                        }
                      />
                    </Box>
                  <Box className="subTitleBox">
                    <Text
                      className="subTitle"
                      children={
                        t(
                          `subscription:SECOND_PAGE.BASKET.TEXT.${opt.code}`
                        ) + " " + ((opt.code === 'premium_pack') ? (formatPrice(opt.price.amount, opt.price.currency) +
                          " / " +
                          t("subscription:MONTH")) : '')
                      }
                    />
                  </Box>
                </Box>
              ))}

            {basket.promo ? <Box className="promo">
              <Box className="text" onClick={handleRemovePromoCode}>
                <Text className="button" children={t("subscription:THIRD_PAGE.MODAL.TITLE") + " : " + basket.promo + " ❌"} />
              </Box>
              <Box className="price">
                <Text children={`-${formatPrice(promocode_amount, sub_currency)}`} />
              </Box>
            </Box> : (
              /* Promo code */
              basket.giftcard === null ? (
                <Box className="text" onClick={handleOpenPromoCode}>
                  <Text className="button orange" children={t("subscription:ADD_A_PROMO_CODE")} />
                </Box>
              ) : null
            )}
            
          </AccordionDetails>
        </Accordion>
      </Box>

      <Box className="totalPrice">
        <Box className="text">
          <Title
            variant="h1"
            children={t("subscription:SECOND_PAGE.BASKET.TOTAL")}
          />
        </Box>
        <Box className="price">
          <Title variant="h1" children={(totalPrice > 0.0) ? formatPrice(totalPrice, sub_currency) : t("subscription:FREE")} />
        </Box>
      </Box>

      {/* Code Promo Modal */ }
  {
    (basket.giftcard === null) ? <ModalPromotionalCode
      isModalOpen={isModalOpenPromoCode}
      title={t("subscription:THIRD_PAGE.MODAL.TITLE")}
      placeholder="Ex : PROMOCODE"
      current={basket.promo}
      handleClose={handleClosePromoCode}
      handleValidate={handleValidatePromoCode}
      mode="promoCode"
    /> : null
  }
    </Box >
  );
};

export default Basket;